/**
 * Subcontractor Calendar Page
 * Shows the subcontractor's own assignments in a calendar view
 */

import { useState, useMemo, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Clock,
  MapPin,
  User,
  Loader2,
  Download
} from 'lucide-react'
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addMonths,
  subMonths,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isToday,
  isSunday
} from 'date-fns'
import { sv } from 'date-fns/locale'
import { supabase } from '../../lib/supabase'
import { getHolidayForDate } from '../../utils/swedishHolidays'
import { useAuth } from '../../lib/auth'
import { getTgWorkStatusName, getTgWorkStatusColor } from '../../services/statusSystemV2'
import type { TgWorkStatus } from '../../types/statusTypes'
import { usePageTracking } from '../../hooks/usePageTracking'
import { ExportCalendarModal } from '../../components/ExportCalendarModal'

interface CalendarAssignment {
  id: string
  assignment_number: number | null
  title: string
  description: string | null
  start_date: string
  start_time: string | null
  end_time: string | null
  status: string
  tg_status: string | null
  customer_name: string
  customer_phone: string | null
  location: string | null
}

// Assignment statuses visible to subcontractors (V2 statuses)
const VISIBLE_STATUSES = [
  'planerad',      // Upcoming confirmed assignments
  'pagaende',      // In progress - needs reporting
  'granskning',    // Waiting for admin review
  'attesterad',    // Approved
  'slutford'       // Completed
]

// TG statuses to show in legend (excludes preliminar_tilldelad and avsagt)
const VISIBLE_TG_STATUSES = [
  'tilldelad',       // Assigned
  'ej_rapporterad',  // Not reported
  'rapporterad',     // Reported
  'komplettering',   // Supplement required
  'godkand'          // Approved
]

export function SubcontractorCalendar() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()

  // Get initial date from navigation state if provided
  const initialDate = (location.state as { initialDate?: Date })?.initialDate
  const [currentMonth, setCurrentMonth] = useState(() => initialDate ? new Date(initialDate) : new Date())
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [statusFilter, setStatusFilter] = useState<'all' | 'upcoming' | 'to_report'>('all')
  const [showExportModal, setShowExportModal] = useState(false)

  // Update month if navigated with a new date
  useEffect(() => {
    if (initialDate) {
      setCurrentMonth(new Date(initialDate))
    }
  }, [initialDate])

  // Track page view
  usePageTracking('calendar')

  // Fetch subcontractor ID
  const { data: subcontractor } = useQuery({
    queryKey: ['my-subcontractor', user?.id],
    queryFn: async () => {
      if (!user?.id) return null
      const { data, error } = await supabase
        .from('subcontractors')
        .select('id, name')
        .eq('user_id', user.id)
        .single()
      if (error) throw error
      return data
    },
    enabled: !!user?.id
  })

  // Fetch assignments for the current month
  const { data: assignments = [], isLoading } = useQuery<CalendarAssignment[]>({
    queryKey: ['subcontractor-calendar', subcontractor?.id, format(currentMonth, 'yyyy-MM')],
    queryFn: async () => {
      if (!subcontractor?.id) return []

      const monthStart = startOfMonth(currentMonth)
      const monthEnd = endOfMonth(currentMonth)

      const { data, error } = await supabase
        .from('assignments')
        .select(`
          id,
          assignment_number,
          title,
          description,
          start_date,
          start_time,
          end_time,
          status,
          location,
          customers (full_name, phone, hide_details_from_tg),
          assignment_subcontractors!inner (subcontractor_id, tg_status)
        `)
        .eq('assignment_subcontractors.subcontractor_id', subcontractor.id)
        .neq('assignment_subcontractors.tg_status', 'avsagt')
        .neq('assignment_subcontractors.tg_status', 'preliminar_tilldelad')
        .gte('start_date', format(monthStart, 'yyyy-MM-dd'))
        .lte('start_date', format(monthEnd, 'yyyy-MM-dd'))
        .in('status', VISIBLE_STATUSES)
        .order('start_date')
        .order('start_time')

      if (error) throw error

      return (data || []).map(a => {
        const tgData = (a.assignment_subcontractors as any)?.[0] || {}
        const customer = a.customers as any
        const hideDetails = customer?.hide_details_from_tg
        return {
          id: a.id,
          assignment_number: a.assignment_number,
          title: a.title,
          description: a.description,
          start_date: a.start_date,
          start_time: a.start_time,
          end_time: a.end_time,
          status: a.status,
          tg_status: tgData.tg_status || null,
          customer_name: hideDetails ? '' : (customer?.full_name || 'Okänd kund'),
          customer_phone: hideDetails ? null : (customer?.phone || null),
          location: typeof a.location === 'string' ? a.location : (a.location as any)?.address || null
        }
      })
    },
    enabled: !!subcontractor?.id
  })

  // Filter assignments based on status filter (using tg_status)
  const filteredAssignments = useMemo(() => {
    if (statusFilter === 'all') return assignments
    if (statusFilter === 'upcoming') {
      // Only show tilldelad (not preliminar_tilldelad)
      return assignments.filter(a => a.tg_status === 'tilldelad')
    }
    if (statusFilter === 'to_report') {
      // V2: ej_rapporterad means TG needs to report, komplettering means TG needs to supplement
      return assignments.filter(a => a.tg_status === 'ej_rapporterad' || a.tg_status === 'komplettering')
    }
    return assignments
  }, [assignments, statusFilter])

  // Get assignments for a specific date
  const getAssignmentsForDate = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd')
    return filteredAssignments.filter(a => a.start_date === dateStr)
  }

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth)
    const monthEnd = endOfMonth(currentMonth)
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 })
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 })
    return eachDayOfInterval({ start: calendarStart, end: calendarEnd })
  }, [currentMonth])

  // Get assignments for selected date
  const selectedDateAssignments = selectedDate ? getAssignmentsForDate(selectedDate) : []

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => direction === 'prev' ? subMonths(prev, 1) : addMonths(prev, 1))
    setSelectedDate(null)
  }

  const goToToday = () => {
    setCurrentMonth(new Date())
    setSelectedDate(new Date())
  }

  if (isLoading && !subcontractor) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-nature-900 dark:text-nature-100">
            Min Kalender
          </h1>
          <p className="text-nature-600 dark:text-nature-400">
            {subcontractor?.name ? `Uppdrag för ${subcontractor.name}` : 'Dina planerade uppdrag'}
          </p>
        </div>

        {/* Status filter and export */}
        <div className="flex flex-wrap gap-2">
          {[
            { key: 'all', label: 'Alla' },
            { key: 'upcoming', label: 'Kommande' },
            { key: 'to_report', label: 'Att rapportera' }
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key as typeof statusFilter)}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                statusFilter === f.key
                  ? 'bg-primary-600 text-white'
                  : 'bg-nature-100 dark:bg-nature-700 text-nature-700 dark:text-nature-300 hover:bg-nature-200 dark:hover:bg-nature-600'
              }`}
            >
              {f.label}
            </button>
          ))}
          <button
            onClick={() => setShowExportModal(true)}
            disabled={filteredAssignments.length === 0}
            className="px-3 py-1.5 text-sm rounded-lg bg-nature-100 dark:bg-nature-700 text-nature-700 dark:text-nature-300 hover:bg-nature-200 dark:hover:bg-nature-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
            title="Exportera till kalender"
          >
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">Exportera</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <div className="lg:col-span-2 bg-white dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700">
          {/* Month navigation */}
          <div className="flex items-center justify-between p-4 border-b border-nature-200 dark:border-nature-700">
            <button
              onClick={() => navigateMonth('prev')}
              className="p-2 hover:bg-nature-100 dark:hover:bg-nature-700 rounded-lg transition-colors"
            >
              <ChevronLeft className="h-5 w-5 text-nature-600 dark:text-nature-400" />
            </button>
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-nature-900 dark:text-nature-100 capitalize">
                {format(currentMonth, 'MMMM yyyy', { locale: sv })}
              </h2>
              <button
                onClick={goToToday}
                className="px-2 py-1 text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded hover:bg-primary-200 dark:hover:bg-primary-900/50"
              >
                Idag
              </button>
            </div>
            <button
              onClick={() => navigateMonth('next')}
              className="p-2 hover:bg-nature-100 dark:hover:bg-nature-700 rounded-lg transition-colors"
            >
              <ChevronRight className="h-5 w-5 text-nature-600 dark:text-nature-400" />
            </button>
          </div>

          {/* Weekday headers */}
          <div className="grid grid-cols-7 border-b border-nature-200 dark:border-nature-700">
            {['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'].map((day, idx) => (
              <div
                key={day}
                className={`p-2 text-center text-sm font-medium ${idx === 6 ? 'text-red-500 dark:text-red-400' : 'text-nature-500 dark:text-nature-400'}`}
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7">
            {calendarDays.map((day, idx) => {
              const dayAssignments = getAssignmentsForDate(day)
              const isCurrentMonth = isSameMonth(day, currentMonth)
              const isSelected = selectedDate && isSameDay(day, selectedDate)
              const hasAssignments = dayAssignments.length > 0
              const holiday = getHolidayForDate(day)
              const isRedDay = holiday?.isRedDay || isSunday(day)

              return (
                <button
                  key={idx}
                  onClick={() => setSelectedDate(day)}
                  className={`
                    min-h-[80px] p-2 border-b border-r border-nature-100 dark:border-nature-700
                    text-left transition-colors relative
                    ${!isCurrentMonth ? 'bg-nature-50 dark:bg-nature-900/50' : 'bg-white dark:bg-nature-800'}
                    ${isSelected ? 'ring-2 ring-primary-500 ring-inset' : ''}
                    ${isToday(day) ? 'bg-primary-50 dark:bg-primary-900/20' : ''}
                    ${(holiday || isSunday(day)) && !isToday(day) ? 'bg-red-50 dark:bg-red-900/10' : ''}
                    hover:bg-nature-50 dark:hover:bg-nature-700/50
                  `}
                >
                  <div className="flex items-start justify-between gap-1">
                    <span className={`
                      text-sm font-medium
                      ${!isCurrentMonth ? 'text-nature-400 dark:text-nature-600' : 'text-nature-900 dark:text-nature-100'}
                      ${isToday(day) ? 'text-primary-600 dark:text-primary-400' : ''}
                      ${isRedDay ? 'text-red-600 dark:text-red-400' : ''}
                    `}>
                      {format(day, 'd')}
                    </span>
                    {holiday && (
                      <span className={`text-[9px] leading-tight text-right truncate max-w-[60px] ${holiday.isRedDay ? 'text-red-500 dark:text-red-400' : 'text-nature-400'}`}>
                        {holiday.name}
                      </span>
                    )}
                  </div>

                  {/* Assignment dots/preview */}
                  {hasAssignments && (
                    <div className="mt-1 space-y-1">
                      {dayAssignments.slice(0, 2).map(a => (
                        <div
                          key={a.id}
                          className="text-xs px-1 py-0.5 rounded truncate"
                          style={{
                            backgroundColor: `${getTgWorkStatusColor(a.tg_status as TgWorkStatus)}20`,
                            color: getTgWorkStatusColor(a.tg_status as TgWorkStatus)
                          }}
                        >
                          {a.start_time?.slice(0, 5)} {a.title}
                        </div>
                      ))}
                      {dayAssignments.length > 2 && (
                        <div className="text-xs text-nature-500 dark:text-nature-400 px-1">
                          +{dayAssignments.length - 2} till
                        </div>
                      )}
                    </div>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        {/* Selected date panel */}
        <div className="bg-white dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700 p-4">
          <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100 mb-2 flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-primary-600" />
            {selectedDate
              ? format(selectedDate, 'd MMMM yyyy', { locale: sv })
              : 'Välj en dag'
            }
          </h3>
          {selectedDate && getHolidayForDate(selectedDate) && (
            <p className={`text-sm mb-4 ${getHolidayForDate(selectedDate)?.isRedDay ? 'text-red-600 dark:text-red-400' : 'text-nature-500'}`}>
              {getHolidayForDate(selectedDate)?.name}
            </p>
          )}

          {!selectedDate ? (
            <p className="text-nature-500 dark:text-nature-400 text-sm">
              Klicka på en dag i kalendern för att se uppdrag
            </p>
          ) : selectedDateAssignments.length === 0 ? (
            <p className="text-nature-500 dark:text-nature-400 text-sm">
              Inga uppdrag denna dag
            </p>
          ) : (
            <div className="space-y-3">
              {selectedDateAssignments.map(assignment => (
                <button
                  key={assignment.id}
                  onClick={() => navigate(`/subcontractor/assignments/${assignment.id}`)}
                  className="w-full text-left p-3 rounded-lg border border-nature-200 dark:border-nature-700 hover:bg-nature-50 dark:hover:bg-nature-700 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h4 className="font-medium text-nature-900 dark:text-nature-100 line-clamp-1">
                      {assignment.title}
                    </h4>
                    <span
                      className="px-2 py-0.5 text-xs rounded-full whitespace-nowrap"
                      style={{
                        backgroundColor: `${getTgWorkStatusColor(assignment.tg_status as TgWorkStatus)}20`,
                        color: getTgWorkStatusColor(assignment.tg_status as TgWorkStatus)
                      }}
                    >
                      {getTgWorkStatusName(assignment.tg_status as TgWorkStatus)}
                    </span>
                  </div>

                  <div className="space-y-1 text-sm text-nature-600 dark:text-nature-400">
                    {assignment.start_time && (
                      <div className="flex items-center gap-2">
                        <Clock className="h-3.5 w-3.5" />
                        <span>
                          {assignment.start_time.slice(0, 5)}
                          {assignment.end_time && ` - ${assignment.end_time.slice(0, 5)}`}
                        </span>
                      </div>
                    )}
                    {assignment.customer_name && (
                      <div className="flex items-center gap-2">
                        <User className="h-3.5 w-3.5" />
                        <span>{assignment.customer_name}</span>
                      </div>
                    )}
                    {assignment.location && (
                      <div className="flex items-center gap-2">
                        <MapPin className="h-3.5 w-3.5" />
                        <span className="truncate">{assignment.location}</span>
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="bg-white dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700 p-4">
        <h4 className="text-sm font-medium text-nature-700 dark:text-nature-300 mb-3">Statusförklaring</h4>
        <div className="flex flex-wrap gap-4">
          {VISIBLE_TG_STATUSES.map(status => (
            <div key={status} className="flex items-center gap-2">
              <span
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: getTgWorkStatusColor(status as TgWorkStatus) }}
              />
              <span className="text-sm text-nature-600 dark:text-nature-400">
                {getTgWorkStatusName(status as TgWorkStatus)}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Export Modal */}
      <ExportCalendarModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        assignments={filteredAssignments}
        monthLabel={format(currentMonth, 'MMMM yyyy', { locale: sv })}
        currentSubcontractorId={subcontractor?.id}
      />
    </div>
  )
}
